import { useEffect, useRef, useState } from 'preact/hooks'
import { useToast } from '@/hooks/useToast'
import { useCompany } from '@/hooks/useCompany'
import { authOrganizationApi } from '@/api/authOrganization'
import type { AuthOrganizationUpdateDto, AuthOrganizationCreateDto } from '@/types/authOrganizationBranding'
import { useForm } from 'react-hook-form'
import { BRAND_CLASSES } from '@/constants/theme'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { AuthConnectionsList } from './AuthConnectionsList'
import { TextBoxField } from '@/components'

interface BrandingFormState {
    logoUrl: string
    primaryColor: string
    pageBackgroundColor: string
    errors: {
        logoUrl?: string
        primaryColor?: string
        pageBackgroundColor?: string
    }
}

interface AuthenticationSettingsProps {
    clientCompanyId: string
    displayName: string
    authOrgId?: string
}

export function AuthenticationSettings({ clientCompanyId, displayName, authOrgId }: AuthenticationSettingsProps) {
    const { showError, showSuccess } = useToast()
    const { refresh } = useCompany()
    const fileInputRef = useRef<HTMLInputElement>(null)
    
    // Track the actual auth org ID (starts with prop, gets set after create)
    const [currentAuthOrgId, setCurrentAuthOrgId] = useState<string | undefined>(authOrgId)
    // Track if Username/Password auth is enabled (to show/hide branding tab)
    const [isUsernamePasswordEnabled, setIsUsernamePasswordEnabled] = useState(false)
    
    // Active tab
    const [activeTab, setActiveTab] = useState<'configuration' | 'methods' | 'branding'>(() => {
        const v = (authOrgId || '').trim().toLowerCase()
        const hasAuthOrg = v && v !== 'null' && v !== 'undefined'
        return hasAuthOrg ? 'methods' : 'configuration'
    })
    
    const [isLoadingOrg, setIsLoadingOrg] = useState<boolean>(() => {
        const v = (authOrgId || '').trim().toLowerCase()
        const hasAuthOrg = v && v !== 'null' && v !== 'undefined'
        return !!hasAuthOrg
    })
    
    const [branding, setBranding] = useState<BrandingFormState>({
        logoUrl: '',
        primaryColor: '#2563EB',
        pageBackgroundColor: '#FFFFFF',
        errors: {}
    })
    
    const [etag, setEtag] = useState<string>('')
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [previewLogoUrl, setPreviewLogoUrl] = useState<string | null>(null)
    const [loadingState, setLoadingState] = useState<null | 'saving' | 'creatingOrg'>(null)
    
    // react-hook-form setup
    type AuthBrandingForm = {
        displayName: string
        domains: string
        primaryColor: string
        pageBackgroundColor: string
    }
    const {
        register,
        control,
        handleSubmit,
        setValue,
        watch,
        getValues,
        formState: { errors, isValid, isDirty },
        reset,
        setError
    } = useForm<AuthBrandingForm>({
        mode: 'onChange',
        defaultValues: {
            displayName: displayName,
            domains: '',
            primaryColor: '#2563EB',
            pageBackgroundColor: '#FFFFFF',
        },
    })

    // Keep preview colors in sync with form values
    const watchedPrimary = watch('primaryColor')
    const watchedBackground = watch('pageBackgroundColor')
    useEffect(() => {
        setBranding(prev => ({ ...prev, primaryColor: watchedPrimary || '#2563EB' }))
    }, [watchedPrimary])
    useEffect(() => {
        setBranding(prev => ({ ...prev, pageBackgroundColor: watchedBackground || '#FFFFFF' }))
    }, [watchedBackground])

    // Keep a live preview URL for the selected file; revoke when changed/cleared
    useEffect(() => {
        if (!selectedFile) {
            setPreviewLogoUrl(null)
            return
        }
        const url = URL.createObjectURL(selectedFile)
        setPreviewLogoUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [selectedFile])

    useEffect(() => {
        const load = async () => {
            const id = (currentAuthOrgId || '').trim()
            if (!id || id.toLowerCase() === 'null' || id.toLowerCase() === 'undefined') return
            setIsLoadingOrg(true)
            const res = await authOrganizationApi.getById(clientCompanyId, id)
            if (res.error || !res.data) return
            const org = res.data
            
            // Store ETag for updates
            setEtag(org.ETag)
            
            // Prefill branding
            const primary = org.Branding.Colors?.Primary || '#2563EB'
            const background = org.Branding.Colors?.PageBackground || '#FFFFFF'
            setBranding(prev => ({
                ...prev,
                logoUrl: org.Branding.LogoUrl || prev.logoUrl,
                primaryColor: primary,
                pageBackgroundColor: background,
            }))
            
            // Prefill form values
            let csv = ''
            if (org.OrgMetadata?.AllowedDomains) {
                const domainsArr = org.OrgMetadata.AllowedDomains
                    .filter(d => d && typeof d === 'string')
                    .map(d => d.trim())
                    .filter(Boolean)
                csv = domainsArr.join(', ')
            }
            reset({ displayName, primaryColor: primary, pageBackgroundColor: background, domains: csv })
            setIsLoadingOrg(false)
        }
        load()
    }, [currentAuthOrgId, clientCompanyId, displayName, reset])

    const isValidHexColor = (color: string): boolean => /^#[0-9A-F]{6}$/i.test(color)
    const isValidDomain = (domain: string): boolean => {
        // Basic domain regex: labels separated by dots, TLD 2+ chars
        return /^([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/.test(domain)
    }

    const handleFileSelect = (event: Event) => {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]
        if (!file) return

        if (!file.type.startsWith('image/')) {
            setBranding(prev => ({ ...prev, errors: { ...prev.errors, logoUrl: 'Please select an image file (PNG, JPEG)' } }))
            return
        }
        // Allow logos up to 2MB
        if (file.size > 2 * 1024 * 1024) {
            setBranding(prev => ({ ...prev, errors: { ...prev.errors, logoUrl: 'File size must be less than 2MB' } }))
            return
        }

        setSelectedFile(file)
        // Clear any previous logo error and rely on previewLogoUrl for immediate preview
        setBranding(prev => ({ ...prev, errors: { ...prev.errors, logoUrl: undefined } }))
        // Mark the form as dirty so Save enables even if only the file changed
        // react-hook-form doesn't track file inputs by default
        // We can set a dummy value to trigger dirty state without affecting payload
        setValue('domains', getValues('domains'), { shouldDirty: true })
    }

    const handleColorChange = (field: 'primaryColor' | 'pageBackgroundColor', value: string) => {
        setValue(field, value, { shouldValidate: true, shouldDirty: true })
    }

    const handleCreateOrganization = async () => {
        try {
            setLoadingState('creatingOrg')
            const { displayName: displayNameValue, domains } = getValues()
            const domainList = (domains || '')
                .split(',')
                .map(d => d.trim())
                .filter(Boolean)

            const createPayload: AuthOrganizationCreateDto = {
                DisplayName: displayNameValue,
                OrgMetadata: {
                    AllowedDomains: domainList,
                },
            }

            const res = await authOrganizationApi.create(clientCompanyId, createPayload)
            if (res.error) {
                if (res.status === 422) {
                    setFormValidationErrors(res, setError)
                }
                showError('Create failed', res.error)
                return
            }

            // Store the newly created auth org ID
            if (res.data?.Id) {
                setCurrentAuthOrgId(res.data.Id)
                setEtag(res.data.ETag)
                showSuccess('Created', 'Authentication organization created. Now configure allowed domains and methods.')
                // Refresh company data to update authOrganizationId in CompanyProvider
                await refresh()
                // Switch to methods tab
                setActiveTab('methods')
            }
        } catch (e) {
            showError('Create failed', e instanceof Error ? e.message : 'Unable to create organization')
        } finally {
            setLoadingState(null)
        }
    }

    const normalizeAuthOrgId = (id?: string) => {
        const v = (id || '').trim().toLowerCase()
        if (!v || v === 'null' || v === 'undefined') return ''
        return v
    }

    const onSubmit = async () => {
        try {
            setLoadingState('saving')
            const { primaryColor, pageBackgroundColor, displayName: displayNameValue } = getValues()
            const normalized = normalizeAuthOrgId(currentAuthOrgId)
            const shouldUpdate = normalized.length > 0
            
            if (shouldUpdate) {
                const updatePayload: AuthOrganizationUpdateDto = {
                    Id: currentAuthOrgId as string,
                    ETag: etag,
                    DisplayName: displayNameValue,
                    OrgMetadata: {
                        AllowedDomains: [],
                    },
                    Branding: {
                        LogoUrl: branding.logoUrl && !branding.logoUrl.startsWith('blob:') ? branding.logoUrl : undefined,
                        Colors: primaryColor || pageBackgroundColor ? {
                            Primary: primaryColor || undefined,
                            PageBackground: pageBackgroundColor || undefined,
                        } : undefined,
                    },
                }
                const res = await authOrganizationApi.update(clientCompanyId, currentAuthOrgId as string, updatePayload, selectedFile || undefined)
                if (res.error) {
                    // Set form field errors for 422 validation errors
                    if (res.status === 422) {
                        setFormValidationErrors(res, setError)
                    } else {
                        showError('Save failed', res.error)
                    }
                    return
                }
                // Update ETag from server response for next update
                if (res.data?.ETag) {
                    setEtag(res.data.ETag)
                }
                // If API returned a persisted logo URL, adopt it for preview
                if (res.data?.Branding.LogoUrl) {
                    setBranding(prev => ({ ...prev, logoUrl: res.data!.Branding.LogoUrl! }))
                }
                // Reset form with the just-saved values to clear dirty state
                reset({
                    displayName: displayNameValue,
                    domains: '',
                    primaryColor: updatePayload.Branding.Colors?.Primary || '#2563EB',
                    pageBackgroundColor: updatePayload.Branding.Colors?.PageBackground || '#FFFFFF',
                })
                // Clear selected file since it's been uploaded
                setSelectedFile(null)
                if (fileInputRef.current) fileInputRef.current.value = ''
                showSuccess('Saved', 'Branding updated successfully')
            }
        } catch (e) {
            showError('Save failed', e instanceof Error ? e.message : 'Unable to save branding')
        } finally {
            setLoadingState(null)
        }
    }

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="mb-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Authentication</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    {!currentAuthOrgId ? 'Set up authentication for this company.' : 'Manage authentication methods and customize branding.'}
                </p>
            </div>

            {/* Tabs - always show */}
            <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
                <nav className="flex gap-6" aria-label="Authentication tabs">
                    <button
                        type="button"
                        onClick={() => setActiveTab('configuration')}
                        className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'configuration'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                            }`}
                    >
                        Configuration
                    </button>
                    {currentAuthOrgId && (
                        <button
                            type="button"
                            onClick={() => setActiveTab('methods')}
                            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'methods'
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                }`}
                        >
                            Authentication Methods
                        </button>
                    )}
                    {currentAuthOrgId && isUsernamePasswordEnabled && (
                        <button
                            type="button"
                            onClick={() => setActiveTab('branding')}
                            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'branding'
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                }`}
                        >
                            Branding
                        </button>
                    )}
                </nav>
            </div>

            {/* Tab content */}
            {activeTab === 'configuration' && (
                <div className="max-w-2xl">
                    {!currentAuthOrgId && (
                        <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Setup Required</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                Configure your authentication organization. Define allowed email domains and then enable authentication methods.
                            </p>
                        </div>
                    )}

                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 space-y-6">
                        <TextBoxField
                            control={control}
                            name="displayName"
                            label="Auth Display Name"
                            required
                            helperText="Name for this authentication organization."
                            maxLength={200}
                            showCharCounter
                            placeholder="Organization Display Name"
                            rules={{
                                required: 'Display name is required',
                                maxLength: { value: 200, message: 'Display name must be 200 characters or less' },
                                validate: v => (!!v?.trim()) || 'Display name cannot be empty'
                            }}
                        />

                        <TextBoxField
                            control={control}
                            name="domains"
                            label="Allowed Email Domains"
                            required
                            helperText="Comma-separated list. Only users with these email domains can sign in."
                            placeholder="e.g. orbitalx.com, example.com"
                            rules={{
                                required: 'Allowed domains are required',
                                validate: (value: string) => {
                                    const parts = (value ?? '').split(',').map(p => p.trim()).filter(Boolean)
                                    if (parts.length === 0) return 'Provide at least one domain'
                                    const invalid = parts.find(d => !isValidDomain(d))
                                    return invalid ? `Invalid domain: ${invalid}` : true
                                }
                            }}
                        />

                        <div className="pt-4 flex justify-end">
                            {!currentAuthOrgId ? (
                                <button
                                    type="button"
                                    onClick={handleCreateOrganization}
                                    disabled={loadingState === 'creatingOrg' || !isValid}
                                    className={`px-4 py-2.5 rounded ${BRAND_CLASSES.BUTTON.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {loadingState === 'creatingOrg' ? 'Creating…' : 'Create Organization'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleSubmit(async () => {
                                        try {
                                            setLoadingState('saving')
                                            const { domains, displayName: displayNameValue } = getValues()
                                            const domainList = (domains || '')
                                                .split(',')
                                                .map(d => d.trim())
                                                .filter(Boolean)

                                            const updatePayload: AuthOrganizationUpdateDto = {
                                                Id: currentAuthOrgId as string,
                                                ETag: etag,
                                                DisplayName: displayNameValue,
                                                OrgMetadata: {
                                                    AllowedDomains: domainList,
                                                },
                                                Branding: branding.logoUrl || branding.primaryColor ? {
                                                    LogoUrl: branding.logoUrl || undefined,
                                                    Colors: {
                                                        Primary: branding.primaryColor || undefined,
                                                        PageBackground: branding.pageBackgroundColor || undefined,
                                                    }
                                                } : { LogoUrl: undefined, Colors: undefined }
                                            }
                                            
                                            const res = await authOrganizationApi.update(clientCompanyId, currentAuthOrgId as string, updatePayload)
                                            if (res.error) {
                                                if (res.status === 422) {
                                                    setFormValidationErrors(res, setError)
                                                } else {
                                                    showError('Save failed', res.error)
                                                }
                                                return
                                            }
                                            
                                            if (res.data?.ETag) {
                                                setEtag(res.data.ETag)
                                            }
                                            
                                            reset({
                                                ...getValues(),
                                                domains: domainList.join(', ')
                                            })
                                            
                                            showSuccess('Saved', 'Allowed domains updated successfully')
                                        } catch (e) {
                                            showError('Save failed', e instanceof Error ? e.message : 'Unable to save domains')
                                        } finally {
                                            setLoadingState(null)
                                        }
                                    })}
                                    disabled={loadingState === 'saving' || !isDirty || !isValid}
                                    className={`px-4 py-2.5 rounded ${BRAND_CLASSES.BUTTON.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {loadingState === 'saving' ? 'Saving…' : isDirty ? 'Save Domains' : 'No Changes'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'methods' && currentAuthOrgId && (
                <AuthConnectionsList
                    clientCompanyId={clientCompanyId}
                    authOrgId={currentAuthOrgId}
                    allowedDomains={watch('domains')?.split(',').map(d => d.trim()).filter(Boolean)}
                    onConnectionsChange={setIsUsernamePasswordEnabled}
                />
            )}

            {activeTab === 'branding' && currentAuthOrgId && isUsernamePasswordEnabled && (
                <div>
                    <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
                        Branding applies to username/password login. Google authentication uses Google's interface.
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                        {/* Configuration panel */}
                        <div className="space-y-8 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-8 lg:min-h-[640px] h-full">
                            <div>
                                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-200">Organization Logo</label>
                                <div className="flex items-center gap-4">
                                    {previewLogoUrl || branding.logoUrl ? (
                                        <img src={previewLogoUrl || branding.logoUrl} alt="Logo" className="h-20 w-auto rounded border border-slate-200 dark:border-slate-700 bg-white" />
                                    ) : (
                                        <div className="h-20 w-44 bg-slate-100 dark:bg-slate-700/60 flex items-center justify-center text-xs text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-700">No logo</div>
                                    )}
                                    <div className="flex flex-col gap-2">
                                        <button type="button" className="px-3 py-2 text-sm rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900" onClick={() => fileInputRef.current?.click()}>
                                            Choose file
                                        </button>
                                        <span className="text-xs text-slate-500 dark:text-slate-400">PNG or JPG, up to 2MB</span>
                                    </div>
                                </div>
                                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" onChange={handleFileSelect} style={{ display: 'none' }} />
                                {branding.errors.logoUrl && <p className="text-xs text-red-600 mt-1">{branding.errors.logoUrl}</p>}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">Primary Color</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" aria-label="Primary color" value={watchedPrimary} onChange={e => handleColorChange('primaryColor', (e.target as HTMLInputElement).value)} />
                                        <input
                                            type="text"
                                            aria-invalid={!!errors.primaryColor}
                                            className={`px-3 py-2 border rounded text-sm w-36 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:border-transparent ${errors.primaryColor ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : `border-slate-300 dark:border-slate-700 ${BRAND_CLASSES.FOCUS.PRIMARY}`}`}
                                            placeholder="#2563EB"
                                            {...register('primaryColor', {
                                                required: 'Primary color is required',
                                                validate: (v) => isValidHexColor(v) || 'Enter a valid hex color (e.g., #2563EB)'
                                            })}
                                        />
                                    </div>
                                    {errors.primaryColor && <p className="text-xs text-red-600 mt-1">{String(errors.primaryColor.message)}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">Background Color</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" aria-label="Background color" value={watchedBackground} onChange={e => handleColorChange('pageBackgroundColor', (e.target as HTMLInputElement).value)} />
                                        <input
                                            type="text"
                                            aria-invalid={!!errors.pageBackgroundColor}
                                            className={`px-3 py-2 border rounded text-sm w-36 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:border-transparent ${errors.pageBackgroundColor ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : `border-slate-300 dark:border-slate-700 ${BRAND_CLASSES.FOCUS.PRIMARY}`}`}
                                            placeholder="#FFFFFF"
                                            {...register('pageBackgroundColor', {
                                                required: 'Background color is required',
                                                validate: (v) => isValidHexColor(v) || 'Enter a valid hex color (e.g., #FFFFFF)'
                                            })}
                                        />
                                    </div>
                                    {errors.pageBackgroundColor && <p className="text-xs text-red-600 mt-1">{String(errors.pageBackgroundColor.message)}</p>}
                                </div>
                            </div>

                            <div className="pt-2 flex justify-end">
                                {(() => {
                                    const normalized = (currentAuthOrgId || '').trim().toLowerCase()
                                    const isEditMode = !!(normalized && normalized !== 'null' && normalized !== 'undefined')
                                    // Consider selecting a new logo file as a change as well
                                    const hasFileChange = !!selectedFile
                                    const anyDirty = isDirty || hasFileChange
                                    const noChanges = isEditMode && !anyDirty
                                    const isSaving = loadingState === 'saving'
                                    const isLoading = isLoadingOrg
                                    const hasValidationErrors = !isValid
                                    const disabled = isSaving || isLoading || hasValidationErrors || noChanges
                                    const label = isSaving ? 'Saving…' : noChanges ? 'No Changes to Save' : 'Save'
                                    return (
                                        <button type="submit" disabled={disabled} className={`px-4 py-2.5 rounded ${BRAND_CLASSES.BUTTON.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`}>
                                            {label}
                                        </button>
                                    )
                                })()}
                            </div>
                        </div>

                        {/* Live preview panel */}
                        <div className="h-full">
                            <div
                                className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm lg:min-h-[640px] h-full flex flex-col"
                                style={{ backgroundColor: branding.pageBackgroundColor || '#FFFFFF' }}
                            >
                                <div className="p-6">
                                    <div className="mx-auto max-w-[420px]">
                                        {/* Mock Auth0 login card */}
                                        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 backdrop-blur p-10 shadow-lg lg:min-h-[500px] flex flex-col">
                                            {/* Header area: logo and title placeholder */}
                                            <div className="flex flex-col items-center text-center pt-1">
                                                {previewLogoUrl || branding.logoUrl ? (
                                                    <img src={previewLogoUrl || branding.logoUrl} alt="Company logo preview" className="h-12 mb-4" />
                                                ) : (
                                                    <div className="h-12 w-48 bg-slate-100/80 dark:bg-slate-700/70 backdrop-blur rounded mb-4" />
                                                )}
                                                {/* Title placeholder bar (blurred) */}
                                                <div className="h-5 w-56 mx-auto rounded-md bg-slate-200/80 dark:bg-slate-700/60 backdrop-blur mb-10" />
                                            </div>
                                            {/* Spacer pushes the form area down toward the bottom, similar to Auth0 layout */}
                                            <div className="flex-1" />
                                            {/* Bottom area: blurred inputs and primary button mimicking Auth0 spacing */}
                                            <div className="space-y-3 mt-2 pb-2">
                                                {/* Email placeholder block */}
                                                <div className="w-full h-11 rounded-md bg-slate-200/80 dark:bg-slate-700/60 backdrop-blur" />
                                                {/* Password placeholder block */}
                                                <div className="w-full h-11 rounded-md bg-slate-200/80 dark:bg-slate-700/60 backdrop-blur" />
                                                {/* Continue button styled with selected color, blurred */}
                                                <div
                                                    className="w-full h-11 rounded-md text-white flex items-center justify-center"
                                                    style={{ backgroundColor: branding.primaryColor || '#2563EB', filter: 'blur(1px)', opacity: 0.88 }}
                                                />
                                                {/* Fine print placeholder */}
                                                <div className="w-40 h-3 mx-auto rounded bg-slate-200/70 dark:bg-slate-700/60 backdrop-blur mt-2" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-6 pb-6">
                                    <div className="mx-auto max-w-sm text-center text-xs text-slate-500 dark:text-slate-400">
                                        <div className="font-medium mb-1">Mock UI Preview</div>
                                        <div>For organization branding representation only, any other customization will not be visible.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    )
}
